import { Box, Button, HStack, IconButton, Text } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { Select } from "@src/components/ui-kit/Select";
import { FilterTemplateEditModal } from "@src/utils/components/filters/components/filter-template-edit-modal";
import { FilterTemplateSaveModal } from "@src/utils/components/filters/components/filter-template-save-modal";
import { useFiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { observer } from "mobx-react-lite";

export const FilterTemplateSelect = observer(function FilterTemplateSelect() {
  const { filters } = useFiltersPanel();

  if (!filters.hasTemplatesFeature) return null;

  const selectedTemplate = filters.selectedTemplate;
  const isModified =
    Boolean(filters.selectedTemplateId) &&
    !filters.isCurrentFiltersMatchingSelectedTemplate;

  const handleSave = () => {
    if (selectedTemplate) {
      filters.saveTemplate(selectedTemplate.name, selectedTemplate.id);
      return;
    }

    filters.openSaveTemplateModal("");
  };

  const handleSaveAs = () => {
    filters.openSaveTemplateModal(
      selectedTemplate ? t`${selectedTemplate.name} (copy)` : "",
    );
  };

  return (
    <Box px="6" py="3">
      <HStack align="center" spacing="2" wrap="wrap">
        <Box flex="1" minW="32">
          <Select
            isLoading={filters.isFetchingTemplates}
            onChange={(value: string) => {
              filters.selectTemplate(value || null);
            }}
            options={filters.templates.map((template) => ({
              value: template.id,
              label: template.name,
            }))}
            placeholder={t`Search saved filters`}
            size="sm"
            value={filters.selectedTemplateId ?? undefined}
            prefix={<Icon name="search-md" />}
          />
        </Box>
        <Button
          isDisabled={
            Boolean(selectedTemplate) &&
            filters.isCurrentFiltersMatchingSelectedTemplate
          }
          isLoading={filters.isSavingTemplate}
          onClick={handleSave}
          size="sm"
        >
          {selectedTemplate ? <Trans>Update</Trans> : <Trans>Save</Trans>}
        </Button>
        {filters.selectedTemplateId && (
          <Button
            isDisabled={filters.isSavingTemplate}
            onClick={handleSaveAs}
            size="sm"
            variant="outline"
          >
            <Trans>Save as</Trans>
          </Button>
        )}
        <IconButton
          aria-label={t`Edit filter templates`}
          icon={<Icon name="settings-01" />}
          onClick={() => {
            filters.openEditTemplatesModal();
          }}
          size="sm"
          variant="ghost"
        />
      </HStack>
      {isModified && (
        <Text color="grey.500" fontSize="xs" mt="1">
          <Trans>Modified</Trans>
        </Text>
      )}
      <FilterTemplateSaveModal
        defaultName={filters.saveTemplateModal.additionalData?.defaultName}
        isLoading={filters.isSavingTemplate}
        isOpen={filters.saveTemplateModal.isOpen}
        onlyForMe={filters.saveTemplateOnlyForMe}
        onClose={filters.saveTemplateModal.onClose}
        onConfirm={(name) => {
          filters.saveTemplate(name);
        }}
        onOnlyForMeChange={filters.setSaveTemplateOnlyForMe}
      />
      <FilterTemplateEditModal />
    </Box>
  );
});
